import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { tokens } from '../../theme';
import { IPrescription } from "../../types";

interface DashboardPieChartProps {
  prescriptions: IPrescription[];
}

interface TotalByGroup {
  group: string;
  total: number;
}

const getPrescriptionsPerStatus = (prescriptions: IPrescription[]): TotalByGroup[] => {
  const prescriptionsPerStatus = prescriptions.reduce((aggregated, item) => {
    const statusIndex = aggregated.findIndex(x => x.group === item.status);
    if (statusIndex >= 0) {
      aggregated[statusIndex].total += 1;
    } else {
      aggregated.push({
        group: item.status,
        total: 1
      } as TotalByGroup);
    }

    return aggregated;
  }, [] as TotalByGroup[]);

  return prescriptionsPerStatus.sort((a, b) => b.total - a.total);
};

const DashboardPieChart = ({ prescriptions }: DashboardPieChartProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const fillColors = [colors.blueAccent[600], colors.greenAccent[600], colors.redAccent[600], colors.grey[500], '#FF8042', '#FFBB28'];
  
  if (!prescriptions) {
    return null;
  }

  const chartData = getPrescriptionsPerStatus(prescriptions);
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const renderCustomizedLegendText = (value: string, entry: any) => {
    const { color, payload } = entry;
    return <span style={{ color }}>{`(${payload.value}) ${value}`}</span>;
  };

  return (
    <Card sx={{
      backgroundColor: colors.primary[400] 
    }}>
      <CardHeader 
        title={
          <Typography 
            variant="h5" 
            fontWeight="bold" 
            color={colors.greenAccent[500]}
          >
            {`Prescriptions by Status: ${Intl.NumberFormat('en-US').format(prescriptions.length)} Rx(s)`}
          </Typography>
        } 
      />
      
      <CardContent>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <PieChart>
              <Legend 
                layout="vertical" 
                align="left" 
                verticalAlign="middle" 
                iconType="circle" 
                formatter={renderCustomizedLegendText}
              />
              <Pie 
                data={chartData} 
                nameKey="group" 
                dataKey="total" 
                cx="50%" 
                cy="50%" 
                outerRadius="96%"
                labelLine={false}
                label={renderCustomizedLabel} 
                isAnimationActive={true}
                animationDuration={2400}
                fill={colors.blueAccent[700]}                
              >
                {chartData.map((item, index) => (<Cell key={`cell-${index}`} fill={fillColors[index % fillColors.length]} />))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardPieChart;